import { html, View } from 'rune-ts';
import klass from './FanLetterList.module.scss';
import { font } from '../../../../shared/typography';
import { FanLetterItemView } from '../FanLetterItem/FanLetterItemView';
import { FanLetterFormView } from './views/FanLetterFormView';
import { calcLineBreaks } from '../../../pages/CreatorShopFanLetter/util/calcLineBreaks';
import { openFanLetterDetail } from '../../../pages/CreatorShopFanLetter/util/openFanLetterDetail';
import querystring from 'querystring';
import { MuiF } from '../../../../../../modules/Mui/F/Function/module/MuiF';
import { FanLetterData } from '../../../../features/FanLetter/type';
import { CreatorTypeS } from '../../../../features/Creator/outbound/share';

export interface FanLetterListData {
  fan_letters: FanLetterData[];
  creator: CreatorTypeS.CreatorMainProfile;
  is_mobile: boolean;
  is_login: boolean;
  list_type: 'masonry' | 'vertical' | 'horizontal';
  item_mode: 'XLarge' | 'Large' | 'Medium' | 'Small';
  grid_columns?: 5 | 4;
  klass?: string;
}

const MAKE_FAKE_LIST = () => {
  return [
    {
      profile_img:
        '//s3.marpple.co/files/u_29089/2025/3/original/aca2633fd48a65d6e869f577944207709441e7b62.png',
      nickname: ET('creator_shop::fan_letters::fake::nickname1'),
      body: ET('creator_shop::fan_letters::fake::body1'),
      maluv_klass: klass.blue_maluv,
    },
    {
      profile_img:
        '//s3.marpple.co/files/u_29089/2025/3/original/2ac6f6aa50bc8752dff386f3e52805169f36b3571.png',
      nickname: ET('creator_shop::fan_letters::fake::nickname2'),
      body: ET('creator_shop::fan_letters::fake::body2'),
      maluv_klass: klass.brown_maluv,
    },
    {
      profile_img:
        '//s3.marpple.co/files/u_29089/2025/3/original/c43847c250ef953e63a455fad68bd0af3e4f36a33.png',
      nickname: ET('creator_shop::fan_letters::fake::nickname3'),
      body: ET('creator_shop::fan_letters::fake::body3'),
      maluv_klass: klass.pink_maluv,
    },
  ] as const;
};

export class FanLetterListView extends View<FanLetterListData> {
  private static MAKE_FAKE_LIST = MAKE_FAKE_LIST;
  private static DEFAULT_MASONRY_ITEM_HEIGHT = 166 - 22;
  private static DEFAULT_MASONRY_TEXT_HEIGHT = 22;
  private static DEFAULT_MASONRY_PHOTO_HEIGHT = 308;
  private static DEFAULT_MASONRY_WIDTH = 292;

  private fanLetterFormView = new FanLetterFormView(this.data);
  private fanLetterItemViews = this.data.fan_letters.map((fan_letter) => {
    return new FanLetterItemView(
      { ...fan_letter },
      {
        profile_horizontal: false,
        mode: this.data.item_mode,
      },
    );
  });

  private masonryGridData: {
    masonry_fan_letters: FanLetterItemView[][];
    masonry_total_heights: [number, number, number, number];
  } = {
    masonry_fan_letters: [[], [], [], []],
    masonry_total_heights: [0, 0, 0, 0],
  };

  public getFanLetterFormView() {
    return this.fanLetterFormView;
  }

  public getFanLetterItemViews() {
    return this.fanLetterItemViews;
  }

  private static fakeLettersHtml = () => {
    return FanLetterListView.MAKE_FAKE_LIST().map(({ profile_img, nickname, body, maluv_klass }) => {
      return html`
        <div class="${klass.fake_item} ${maluv_klass}">
          <div class="${klass.fake_top}">
            <img class="${klass.fake_img}" src="${profile_img}" alt="" />
          </div>
          <div class="${klass.fake_bottom}">
            <div class="${klass.fake_nickname} ${font('14_bold')}">${nickname}</div>
            <div class="${klass.fake_body} ${font('14_medium')}">${html.preventEscape(body)}</div>
          </div>
        </div>
      `;
    });
  };

  private _setMasonryFanLetters() {
    this.getFanLetterItemViews().forEach((fanLetterView) => {
      const { photos, body = '' } = fanLetterView.data;
      const line_height =
        Math.min(3, calcLineBreaks(body || '', FanLetterListView.DEFAULT_MASONRY_WIDTH)) *
        FanLetterListView.DEFAULT_MASONRY_TEXT_HEIGHT;
      const total_height =
        FanLetterListView.DEFAULT_MASONRY_ITEM_HEIGHT +
        line_height +
        (photos?.[0]?.id ? FanLetterListView.DEFAULT_MASONRY_PHOTO_HEIGHT : 0);
      const min_grid_idx = this.masonryGridData.masonry_total_heights.indexOf(
        Math.min(...this.masonryGridData.masonry_total_heights),
      );
      this.masonryGridData.masonry_total_heights[min_grid_idx] =
        (this.masonryGridData.masonry_total_heights[min_grid_idx] || 0) + total_height;
      const fan_letter_list_arr = this.masonryGridData.masonry_fan_letters[min_grid_idx];
      if (fan_letter_list_arr) {
        fan_letter_list_arr.push(fanLetterView);
      }
    });
    return this;
  }

  private _makeFanLetterItemsHtml(is_fan_letters: boolean) {
    const { list_type, is_mobile } = this.data;
    if (is_fan_letters) {
      if (list_type === 'masonry') {
        return html` <div class="${klass.fan_letters_items_masonry}">
          ${this._setMasonryFanLetters().masonryGridData.masonry_fan_letters.map((fanLetterItemViews) => {
            return html` <div class="${klass.fan_letter_items_vertical}">${fanLetterItemViews}</div> `;
          })}
        </div>`;
      } else if (list_type === 'vertical') {
        return html`<div class="${klass.fan_letter_items_vertical}">${this.getFanLetterItemViews()}</div>`;
      } else if (list_type === 'horizontal') {
        if (is_mobile) {
          /* swiper */
          return html`
            <div class="${klass.fan_letters_items} ${klass.fan_letters_item_swipe}">
              ${this.getFanLetterItemViews()}
            </div>
          `;
        } else {
          return this.getFanLetterItemViews();
        }
      }
    } else {
      if (is_mobile && list_type !== 'vertical') {
        return html` <div class="${klass.fan_letters_items} ${klass.fan_letters_item_swipe}">
          ${FanLetterListView.fakeLettersHtml()}
        </div>`;
      }
      return FanLetterListView.fakeLettersHtml();
    }
  }

  private _getGridColumns() {
    switch (this.data.grid_columns) {
      case 5:
        return klass.pc_grid_5;
      case 4:
        return klass.pc_grid_4;
      default:
        return klass.pc_grid_5;
    }
  }

  protected override template() {
    const { fan_letters, list_type, is_mobile, item_mode } = this.data;
    const is_fan_letters = fan_letters.length > 0;
    /* pc 가로, 라지사이즈는 그리드 */
    const is_pc_grid = !is_mobile && list_type === 'horizontal' && item_mode == 'Large';

    return html`
      <div class="${this.data.klass}">
        <div
          class="${klass.fan_letters_list} ${is_pc_grid
            ? `${klass.pc_grid} ${this._getGridColumns()}`
            : ''} ${!is_fan_letters ? klass.no_fan_letters : ''}"
        >
          ${this.fanLetterFormView}${this._makeFanLetterItemsHtml(is_fan_letters)}
        </div>
      </div>
    `;
  }

  protected override onRender() {
    const query = querystring.parse(location.search.slice(1));
    const { a_id } = query;
    const fan_letter_id = Number(a_id);
    if (fan_letter_id) {
      delete query.a_id;
      const query_str = querystring.stringify(query);
      MuiF.extendHistoryState(null, null, `${location.pathname}${query_str ? `?${query_str}` : ''}`, true);

      return openFanLetterDetail({
        fan_letter_id,
        fan_letter_item_view: this.fanLetterItemViews.find(({ data }) => {
          return data.id == fan_letter_id;
        }),
      });
    }
  }
}
